import type { Banner } from '@commerce/core'

/**
 * The home's banner rail, from `session.banners`.
 *
 * The schema carries no `link` or `targetUrl`: `reference` is a bare `String`
 * and `type` is the only thing that says how to read it. Both were established
 * rather than guessed —
 *
 * - `BannerType` and `UserAgentType` were introspected against
 *   `storefront-user-stg.yalochat.com/v3/user/storefronts`. `BannerType` is
 *   `CATEGORY | CATEGORY_NAV | EXTERNAL_LINK | GENERAL | PARENT_CATEGORY |
 *   PRODUCT | SUBCATEGORY_NAV`; `UserAgentType` is `ANDROID | IOS | WEB | ALL`.
 * - what each type does with `reference` comes from cinnamon's
 *   `hooks/useBannersClick.ts`, the production client: `PRODUCT` opens the
 *   product detail for `reference` as a **sku**, the four category types route
 *   to `reference` as a **category**, `EXTERNAL_LINK` calls
 *   `window.open(reference)` so it is a **URL**, and `GENERAL` falls to
 *   `default: break` — a banner that goes nowhere. `utils/tests/castBanners.test.ts`
 *   corroborates it: its `PRODUCT` fixture references `YAL100`, its `GENERAL`
 *   one references `''`.
 *
 * What this screen can honour is narrower than what cinnamon can, and the gap
 * is closed by refusing to guess: a destination that does not resolve makes the
 * banner a plain image rather than a tap that lands somewhere invented. See
 * `docs/NOT_PORTED.md`.
 */

/** Where a banner sends the shopper, once `reference` has been read against `type`. */
export type BannerDestination =
  | { readonly kind: 'product'; readonly sku: string }
  | { readonly kind: 'category'; readonly name: string }
  | { readonly kind: 'externalLink'; readonly url: string }

/** One banner as the rail draws it. */
export interface HomeBanner {
  readonly id: string
  readonly imageUrl: string
  /** `null` for a banner with nowhere this screen can send the shopper. */
  readonly destination: BannerDestination | null
}

export interface HomeBannerContext {
  /**
   * The clock. Injected so the date window is testable without freezing the
   * global one, and so the page decides how often "now" moves.
   */
  readonly now: Date
  /**
   * The category names the chip rail actually offers. A category banner is only
   * tappable when it names one of these — see `bannerDestination`.
   */
  readonly categories: readonly string[]
}

/**
 * The four `BannerType`s whose `reference` is a category. cinnamon sends each
 * to a different screen — scroll in place, the category page, the subcategory
 * page — and `PARENT_CATEGORY` to none at all, because it has no case for it.
 * This storefront has exactly one category surface, the chip rail, so all four
 * resolve to the same place: the products of the category `reference` names.
 * That is a reduction of cinnamon's routing, not an invention — the destination
 * is the same category either way.
 */
const CATEGORY_TYPES = new Set(['CATEGORY', 'CATEGORY_NAV', 'SUBCATEGORY_NAV', 'PARENT_CATEGORY'])

/**
 * The `UserAgentType` values that are not this client. `WEB` and `ALL` are, and
 * so is anything else — including the `null` cinnamon's own fixtures carry, and
 * any value added to the enum after this was written. Only an explicit foreign
 * platform hides a banner, which is the same rule `isActive` follows below:
 * absent is not the same as excluded.
 */
const FOREIGN_PLATFORMS = new Set(['ANDROID', 'IOS'])

/**
 * `startDate` and `expirationDate` are typed `Date` by core and arrive as ISO
 * strings over the wire, so both are accepted. `null` means "no bound at this
 * end", and so does a value that does not parse — a banner is not hidden
 * because its dates are malformed.
 */
const timeOf = (value: Date | string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime()
  return Number.isNaN(time) ? null : time
}

/** Whether `now` falls inside the banner's scheduled window, open at either end. */
export const isWithinWindow = (banner: Banner, now: Date): boolean => {
  const at = now.getTime()
  const start = timeOf(banner.startDate)
  const end = timeOf(banner.expirationDate)
  if (start !== null && at < start) return false
  if (end !== null && at > end) return false
  return true
}

/**
 * An `EXTERNAL_LINK` reference, only if it is really an absolute web URL.
 *
 * cinnamon hands `reference` straight to `window.open`, which will happily run
 * a `javascript:` reference and will resolve a relative one against the
 * storefront's own origin. Neither is a destination anybody authored on
 * purpose, so both become "no destination" here.
 */
const webUrl = (reference: string): string | null => {
  try {
    const url = new URL(reference)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null
  } catch {
    return null
  }
}

/**
 * Where one banner goes, or `null` when this screen cannot reach it.
 *
 * A category has to name a category the rail is offering: the chip is the only
 * way to select one, so a reference the rail has no chip for could only leave
 * the shopper on an empty grid. A sku is not checked the same way — the grid
 * holds one page of a server-side catalog, so "not on screen" does not mean
 * "not in the catalog", and the product page answers for a sku it cannot find.
 */
export const bannerDestination = (
  banner: Banner,
  categories: readonly string[],
): BannerDestination | null => {
  const reference = (banner.reference ?? '').trim()
  if (reference === '') return null

  if (banner.type === 'PRODUCT') return { kind: 'product', sku: reference }

  if (CATEGORY_TYPES.has(banner.type)) {
    return categories.includes(reference) ? { kind: 'category', name: reference } : null
  }

  if (banner.type === 'EXTERNAL_LINK') {
    const url = webUrl(reference)
    return url === null ? null : { kind: 'externalLink', url }
  }

  // GENERAL, and any type added to the enum after this was written.
  return null
}

/**
 * What a tappable banner is called.
 *
 * The image itself has no alt text anywhere in the data, so the destination is
 * the only thing about a banner we actually know — and it is also the only
 * thing the tap does. Naming the action rather than describing the picture is
 * therefore the honest choice: "Ver Bebidas" is true, and a guess at what the
 * artwork depicts would not be.
 */
export const bannerActionLabel = (destination: BannerDestination): string => {
  switch (destination.kind) {
    case 'product':
      return `Ver producto ${destination.sku}`
    case 'category':
      return `Ver ${destination.name}`
    case 'externalLink':
      // The host is what tells two link banners apart, and warning about the
      // new tab in the name is the only warning a screen reader will get.
      return `Abrir ${new URL(destination.url).hostname} en una pestaña nueva`
  }
}

/**
 * The banners this shopper should see, in the order the storefront asked for.
 *
 * Dropped, in order: an explicitly inactive banner, one aimed at a platform
 * that is not the web, one outside its date window, and one with no image —
 * a banner *is* an image, so there is nothing left to draw without one.
 *
 * An empty result means no rail at all. The caller renders nothing rather than
 * an empty strip, the same way it treats the reels and the suggested basket.
 */
export const homeBanners = (
  banners: readonly Banner[] | null | undefined,
  { now, categories }: HomeBannerContext,
): HomeBanner[] =>
  (banners ?? [])
    .filter((banner) => banner.isActive !== false)
    .filter((banner) => !FOREIGN_PLATFORMS.has(banner.userAgent ?? ''))
    .filter((banner) => isWithinWindow(banner, now))
    .map((banner) => ({ banner, imageUrl: (banner.imagesURL?.[0] ?? '').trim() }))
    .filter((entry) => entry.imageUrl !== '')
    // Lowest priority first, matching the reels. `filter` already copied the
    // array, so the caller's order is untouched.
    .sort((left, right) => (left.banner.priority ?? 0) - (right.banner.priority ?? 0))
    .map(({ banner, imageUrl }) => ({
      id: banner.id,
      imageUrl,
      destination: bannerDestination(banner, categories),
    }))
